import React from 'react'
import { styled } from 'styled-components'
import type { PastAnalyticsPerformanceScores } from '@mission.io/mission-player'
import { CATEGORIES, MAX_PREVIOUS_MISSIONS_TO_SHOW } from './constants'
import { getFormattedDate } from './helpers'
import { ColorCoin } from './ProficiencyChart/sharedComponents'
import { translateScore, getBaseProficiency } from './ProficiencyChart/helpers'
import { cssPlayerHeightUnit } from '../../../../utility'

/**
 * PreviousMissions - a react component which displays the previous mission scores (Performance and points)
 *
 * Any updates made to this component CANNOT include useContext
 * @param {Object} props - the react props
 * @param  {?Array<PastAnalyticsPerformanceScores>} props.pastAnalyticsPerformanceScores - the previous mission performance scores to display
 *
 * @returns React$Node
 */
export default function PreviousMissions({
	pastAnalyticsPerformanceScores,
}: {
	pastAnalyticsPerformanceScores: ?(PastAnalyticsPerformanceScores[]),
}): React$Node {
	if (!pastAnalyticsPerformanceScores?.length) {
		return null
	}

	const analyticsToShow = pastAnalyticsPerformanceScores.slice(
		0,
		Math.min(
			pastAnalyticsPerformanceScores.length,
			MAX_PREVIOUS_MISSIONS_TO_SHOW
		)
	)

	return (
		<Wrapper>
			<h1 className="font-game-headline">Last Mission</h1>
			{analyticsToShow.map(
				(
					{
						missionName,
						createdTime,
						performanceScores,
						analyticsOverview,
					}: PastAnalyticsPerformanceScores,
					index: number
				) => (
					<div key={index}>
						<Row>
							<Label>Date: {getFormattedDate(new Date(createdTime))}</Label>
							<Label>Mission: {missionName}</Label>
						</Row>
						<ScoreContainer>
							<div>
								<Title>Average Score</Title>
								<Score>
									{analyticsOverview?.averageStudentScore
										? Math.round(analyticsOverview.averageStudentScore)
										: 'N/A'}
								</Score>
							</div>
							{CATEGORIES.map(({ lookup, id, title }) => {
								const proficiencyLevel = getBaseProficiency(
									translateScore(
										performanceScores ? lookup(performanceScores) : null
									)
								)

								return (
									<div key={id}>
										<Title>{title}</Title>
										<ColorCoin level={proficiencyLevel}>
											{proficiencyLevel ?? 'N/A'}
										</ColorCoin>
									</div>
								)
							})}
						</ScoreContainer>
					</div>
				)
			)}
		</Wrapper>
	)
}

const Wrapper = styled.div`
	width: 100%;
	margin-top: 4em;
`

const Label = styled.div`
	padding: 4px;
	border-radius: 4px;
	margin: 8px 0;
	&:nth-child(2n + 1) {
		background: #b1519c;
	}
	&:nth-child(2n) {
		background: #4f9d99;
	}
	color: white;
	&:not(:last-child) {
		margin-right: 8px;
	}
	font-size: 1.75em;
	margin-top: 2em;
`

const ScoreContainer = styled.div`
	margin-bottom: 2em;
	border-radius: 4px;
	display: flex;
	background: #3f3b6e;
	justify-content: space-between;
	> * {
		flex: 1;
		padding: 16px;
		&:not(:first-child) {
			background: #5b5889;
		}
		&:not(:last-child) {
			border-right: 6px solid #3f3b6e;
		}
		text-align: center;
		color: white;
		:last-child {
			border-radius: 0 4px 4px 0;
		}
	}
	${ColorCoin} {
		width: ${cssPlayerHeightUnit(4)};
		display: flex;
		justify-content: center;
		align-items: center;
		margin: 0 auto;

		font-size: 2.25em;
	}
`

const Title = styled.div`
	font-size: 2em;
	margin-bottom: 0.5em;
`

const Score = styled.div`
	font-size: 2.25em;
`

const Row = styled.div`
	display: flex;
`
